import { useCallback, useEffect, useState } from 'react';
import { api, type Company, type NewProvision, type ProvisionView } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date as formatDate, money, periodShort } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';
import type { Panel } from './Shell';

const previousMonth = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Rozliczenia międzyokresowe bierne (art. 39 ust. 2 ustawy o rachunkowości).
 *
 * Odwrotność harmonogramu czynnego, więc panel stoi obok niego przy kosztach — ale formularz jest
 * inny i to jest tu najważniejsze: **nie ma dokumentu do wskazania**. Faktury jeszcze nie ma, więc
 * kwotę, konto i miesiące podaje człowiek, a obok kwoty stoi pole „z czego to wyszło”, obowiązkowe
 * i widoczne w tabeli: bez niego przy badaniu sprawozdania nie ma czym obronić szacunku.
 *
 * Saldo rezerwy pokazujemy zawsze, nawet gdy wynosi zero: rezerwa, której nikt nie rozliczył po
 * skończeniu okresu, to jedna z rzeczy, których nikt nie zauważa samodzielnie.
 */
export function ProvisionsPanel({
  company,
  t,
  locale,
  editable,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  editable: boolean;
  onGo?: (panel: Panel) => void;
}) {
  const [rows, setRows] = useState<ProvisionView[] | null>(null);
  const [form, setForm] = useState<NewProvision | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [month, setMonth] = useState(previousMonth);
  const [message, setMessage] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<{ documentNumber: string; reason: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api.provisions(company.id));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  if (form) {
    return (
      <div className="panel">
        <header>
          <h4>{t.provisions.add}</h4>
          <div className="sp">
            <button type="button" className="btn ghost sm" onClick={() => setForm(null)}>
              {t.provisions.cancel}
            </button>
          </div>
        </header>
        <p className="sm muted">{t.provisions.addHint}</p>
        <ErrorAlert error={error} t={t} onGo={onGo} />
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.addProvision(company.id, {
                ...form,
                provisionAccount: form.provisionAccount?.trim() || null,
              });
              setForm(null);
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="prDesc">{t.provisions.description}</label>
              <input
                id="prDesc"
                className="inp"
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="prBasis">{t.provisions.basis}</label>
              <input
                id="prBasis"
                className="inp"
                value={form.basis}
                onChange={(event) => setForm({ ...form, basis: event.target.value })}
                required
              />
              <p className="hint">{t.provisions.basisHint}</p>
            </div>
            <div className="field">
              <label htmlFor="prAmount">{t.provisions.amount}</label>
              <input
                id="prAmount"
                className="inp n"
                inputMode="decimal"
                value={form.totalAmount}
                onChange={(event) => setForm({ ...form, totalAmount: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="prFrom">{t.provisions.from}</label>
              <input
                id="prFrom"
                className="inp"
                type="month"
                value={form.fromPeriod}
                onChange={(event) => setForm({ ...form, fromPeriod: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="prTo">{t.provisions.to}</label>
              <input
                id="prTo"
                className="inp"
                type="month"
                value={form.toPeriod}
                onChange={(event) => setForm({ ...form, toPeriod: event.target.value })}
                required
              />
              <p className="hint">{t.provisions.periodHint}</p>
            </div>
            <div className="field">
              <label htmlFor="prCost">{t.provisions.costAccount}</label>
              <input
                id="prCost"
                className="inp mono"
                value={form.costAccount}
                onChange={(event) => setForm({ ...form, costAccount: event.target.value })}
                required
              />
              <p className="hint">{t.provisions.costAccountHint}</p>
            </div>
            <div className="field">
              <label htmlFor="prAccount">{t.provisions.provisionAccount}</label>
              <input
                id="prAccount"
                className="inp mono"
                value={form.provisionAccount ?? ''}
                onChange={(event) => setForm({ ...form, provisionAccount: event.target.value })}
              />
              <p className="hint">{t.provisions.provisionAccountHint}</p>
            </div>
          </div>
          <div className="row">
            <button type="submit" className="btn pri" disabled={busy}>
              {t.provisions.save}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="panel">
      <header>
        <h4>{t.provisions.title}</h4>
        <div className="sp">
          {editable && (
            <>
              <input
                className="inp sm"
                type="month"
                aria-label={t.provisions.month}
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              />
              <button
                type="button"
                className="btn ghost sm"
                disabled={busy || !month}
                onClick={() =>
                  void run(async () => {
                    const result = await api.accrueProvisions(company.id, month);
                    setMessage(
                      `${t.provisions.accrued}: ${result.booked} · ${money(result.total, locale)} (${periodShort(result.period)})`,
                    );
                    setSkipped(result.skipped);
                  })
                }
              >
                {t.provisions.accrue}
              </button>
              <button
                type="button"
                className="btn sm"
                disabled={busy}
                onClick={() =>
                  setForm({
                    description: '',
                    basis: '',
                    totalAmount: '',
                    fromPeriod: previousMonth(),
                    toPeriod: previousMonth(),
                    costAccount: '',
                  })
                }
              >
                <Icon.Plus />
                {t.provisions.add}
              </button>
            </>
          )}
        </div>
      </header>

      <p className="sm muted">{t.provisions.lead}</p>
      <ErrorAlert error={error} t={t} onGo={onGo} />
      {message && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{message}</b>
          </div>
        </div>
      )}
      {/* Pominięte rezerwy wymieniamy z powodu: cisza znaczyłaby „wszystko naliczone" */}
      {skipped.length > 0 && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.provisions.skipped}</b>
            <ul>
              {skipped.map((row) => (
                <li key={`${row.documentNumber}-${row.reason}`}>
                  {row.documentNumber}: {row.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {rows?.length === 0 ? (
        <div className="empty">
          <Icon.Calendar />
          <b>{t.provisions.none}</b>
          <p>{t.provisions.noneHint}</p>
        </div>
      ) : (
        rows && (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.provisions.description}</th>
                  <th>{t.provisions.basis}</th>
                  <th className="n">{t.provisions.amount}</th>
                  <th>{t.provisions.period}</th>
                  <th className="n">{t.provisions.accruedTotal}</th>
                  <th className="n">{t.provisions.balance}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.provision.id}>
                    <td>{row.provision.description}</td>
                    <td className="sm muted">{row.provision.basis}</td>
                    <td className="n">{amount(row.provision.totalAmount, locale)}</td>
                    <td className="doc">
                      {row.provision.fromPeriod === row.provision.toPeriod
                        ? periodShort(row.provision.fromPeriod)
                        : `${periodShort(row.provision.fromPeriod)}–${periodShort(row.provision.toPeriod)}`}
                    </td>
                    <td className="n">
                      {amount(row.accruedTotal, locale)}
                      {row.accruedThrough && (
                        <span className="sm muted">
                          {' '}
                          {t.provisions.through} {periodShort(row.accruedThrough)}
                        </span>
                      )}
                    </td>
                    <td className="n">
                      {amount(row.balance, locale)}
                      {Number(row.balance) === 0 && Number(row.accruedTotal) > 0 && (
                        <span className="chip st-ok">
                          <i />
                          {t.provisions.closed}
                        </span>
                      )}
                    </td>
                    <td className="act">
                      <div className="row">
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => setOpen(open === row.provision.id ? null : row.provision.id)}
                        >
                          {open === row.provision.id ? t.provisions.hidePlan : t.provisions.showPlan}
                        </button>
                        {editable && Number(row.balance) > 0 && (
                          <button
                            type="button"
                            className="btn sm"
                            disabled={busy}
                            onClick={() => {
                              /*
                               * Konto pytamy, a nie zgadujemy: rezerwa rozliczona fakturą schodzi na
                               * to samo konto kosztowe, a rezerwa, której zobowiązanie nie powstało —
                               * na pozostałe przychody operacyjne. To dwie różne rzeczy.
                               */
                              const account = window.prompt(t.provisions.settleAccount, row.provision.costAccount);
                              if (account === null) return;
                              void run(() =>
                                api.settleProvision(company.id, row.provision.id, {
                                  postingDate: today(),
                                  account: account.trim() || null,
                                }),
                              );
                            }}
                          >
                            {t.provisions.settle}
                          </button>
                        )}
                        {editable && Number(row.accruedTotal) === 0 && (
                          <button
                            type="button"
                            className="btn danger ico sm"
                            aria-label={t.provisions.remove}
                            title={t.provisions.remove}
                            disabled={busy}
                            onClick={() => {
                              const reason = window.prompt(t.provisions.removeReason);
                              if (reason) void run(() => api.removeProvision(company.id, row.provision.id, reason));
                            }}
                          >
                            <Icon.Trash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {open && rows && (
        <ProvisionDetail row={rows.find((row) => row.provision.id === open)} t={t} locale={locale} />
      )}
    </div>
  );
}

/** Naliczenia i rozliczenia jednej rezerwy — dwie tabele, bo to dwie różne rzeczy w księgach. */
function ProvisionDetail({
  row,
  t,
  locale,
}: {
  row: ProvisionView | undefined;
  t: Dictionary;
  locale: Locale;
}) {
  if (!row) return null;
  return (
    <>
      <div className="tbl">
        <table>
          <caption className="sm muted">
            {t.provisions.plan}: {row.provision.description}
          </caption>
          <thead>
            <tr>
              <th>{t.provisions.month}</th>
              <th className="n">{t.provisions.instalment}</th>
              <th className="n">{t.provisions.remaining}</th>
              <th>{t.provisions.entry}</th>
            </tr>
          </thead>
          <tbody>
            {row.plan.map((instalment) => (
              <tr key={instalment.period}>
                <td className="doc">{periodShort(instalment.period)}</td>
                <td className="n">{amount(instalment.amount, locale)}</td>
                <td className="n">{amount(instalment.remaining, locale)}</td>
                <td>
                  {instalment.entryNo === null ? (
                    <span className="sm muted">{t.provisions.waiting}</span>
                  ) : (
                    <span className="mono">{instalment.entryNo}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {row.settlements.length > 0 && (
        <div className="tbl">
          <table>
            <caption className="sm muted">{t.provisions.settlements}</caption>
            <thead>
              <tr>
                <th>{t.provisions.settledOn}</th>
                <th className="n">{t.provisions.settledAmount}</th>
                <th>{t.provisions.settledAccount}</th>
                <th>{t.provisions.entry}</th>
              </tr>
            </thead>
            <tbody>
              {row.settlements.map((settlement) => (
                <tr key={settlement.entryNo}>
                  <td className="doc">{formatDate(settlement.postingDate, locale)}</td>
                  <td className="n">{amount(settlement.amount, locale)}</td>
                  <td className="mono">{settlement.account}</td>
                  <td className="mono">{settlement.entryNo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
